import React, { useState } from 'react';
import { connect } from 'react-redux';
import EditableTextEditor from './EditableTextEditor';
import { setValueAtPath } from '../../../redux/actions';
import { selectValueAtPath, SelectReportState } from '../Selector';

// A single shared wrapper for any editable region in the report.
//
// Props:
//   path         Dot-path into the sampleData-shape (see dataPaths.js)
//   as           Tag used in view mode (default "div"; use "span" for inline)
//   className    Forwarded to the rendered element
//   variant      "inline" (default) or "block" — controls toolbar + formats
//   fallback     Plain string to display when the looked-up value is empty
//   stripInlineWrap   When true, strips a single wrapping <p> in view mode
//                     (useful when the editable region lives inside an element
//                     whose tag already provides block semantics)
function EditableText(props) {
  const {
    path,
    as: As = 'div',
    className,
    variant = 'inline',
    fallback = '',
    stripInlineWrap = false,
    style,
    editable,
    value,
    setValueAtPath,
  } = props;
  const [editing, setEditing] = useState(false);
  const [hover, setHover] = useState(false);

  const displayHtml = (() => {
    let html = value != null && value !== '' ? value : fallback;
    if (!html) return '';
    if (stripInlineWrap) {
      // Strip a single outer <p>...</p> wrap so inline text doesn't get
      // unwanted paragraph margins when the host tag already supplies layout.
      const trimmed = String(html).trim();
      const m = trimmed.match(/^<p[^>]*>([\s\S]*)<\/p>$/i);
      if (m) return m[1];
    }
    return html;
  })();

  if (!editable) {
    return (
      <As
        className={className}
        style={style}
        dangerouslySetInnerHTML={{ __html: displayHtml }}
      />
    );
  }

  const handleCommit = (next) => {
    setEditing(false);
    if (next !== value) {
      setValueAtPath(path, next);
    }
  };

  const handleCancel = () => setEditing(false);

  const classes = [
    className,
    'editable',
    hover && !editing ? 'editable--hover' : '',
    editing ? 'editable--editing' : '',
  ]
    .filter(Boolean)
    .join(' ');

  if (editing) {
    return (
      <As
        className={classes}
        style={style}
        data-editable
        data-path={path}
        data-variant={variant}
      >
        <EditableTextEditor
          value={value || fallback || ''}
          variant={variant}
          onCommit={handleCommit}
          onCancel={handleCancel}
        />
      </As>
    );
  }

  return (
    <As
      className={classes}
      style={style}
      data-editable
      data-path={path}
      data-variant={variant}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={(e) => {
        e.stopPropagation();
        setEditing(true);
      }}
      dangerouslySetInnerHTML={{ __html: displayHtml }}
    />
  );
}

const mapStateToProps = (state, ownProps) => ({
  editable: SelectReportState(state).editable,
  value: selectValueAtPath(state, ownProps.path),
});

const mapDispatchToProps = (dispatch) => ({
  setValueAtPath: (path, value) => dispatch(setValueAtPath(path, value)),
});

export default connect(mapStateToProps, mapDispatchToProps)(EditableText);
